import { toRefs, ref, computed } from 'vue';
import tinycolor from 'tinycolor2';
import uniqueId from 'lodash/uniqueId';
import { useTheme } from '../../compositions/component-lib.esm7.js';
import { useModel } from '../../compositions/component-lib.esm15.js';
import { getThemeColor } from '../../styles/theme/component-lib.esm3.js';

//
const InputProps = {
  id: String,
  component: {
    type: String,
    default: 'input'
  },
  classes: {
    type: Object,
    default: () => ({})
  },
  rootId: String,
  name: String,
  value: [String, Number],
  type: {
    type: String,
    default: 'text'
  },
  borderColor: {
    type: String,
    default: 'gray300'
  },
  color: {
    type: String,
    default: 'primary'
  },
  disabled: Boolean,
  readonly: Boolean,
  maxlength: String,
  placeholder: String,
  autocomplete: String,
  pattern: String,
  inputListeners: {
    type: Object,
    default: () => ({})
  },
  hasError: Boolean,
  align: String,
  prefix: String,
  suffix: String,
  ariaDisabled: String,
  ariaInvalid: String,
  ariaRequired: String,
  ariaErrorMessage: String
};
var script = {
  name: 'Input',
  props: InputProps,
  computed: {
    listeners() {
      // eslint-disable-next-line no-unused-vars
      const {
        input,
        focus,
        blur,
        ...rest
      } = this.$listeners;
      return rest;
    }
  },
  setup(props, _ref) {
    let {
      emit,
      attrs
    } = _ref;
    const {
      component,
      value,
      type,
      borderColor,
      color,
      disabled,
      readonly,
      maxlength,
      placeholder,
      autocomplete,
      pattern,
      hasError,
      id,
      ariaDisabled,
      ariaErrorMessage,
      ariaInvalid,
      ariaRequired
    } = toRefs(props);
    const theme = useTheme();
    const input = ref(null);
    const inputId = ref(id.value || uniqueId('input'));
    const isFocused = ref(false);
    const localValue = useModel(value, val => emit('input', val));
    const isTextarea = computed(() => component.value === 'textarea');
    const inputClasses = computed(() => {
      return {
        'v-input--disabled': disabled.value,
        'v-input--error': hasError.value,
        'v-input--focus': isFocused.value,
        'v-input--readonly': readonly.value,
        'v-input--multiline': isTextarea.value
      };
    });
    const styles = computed(() => {
      let errorVars = {};
      const highlightColor = getThemeColor(color.value, theme.value);
      const focusColor = tinycolor(highlightColor).setAlpha(0.025).toString();
      if (hasError.value) {
        errorVars = {
          '--ci-color-bg-error': tinycolor(theme.value.colors.error).setAlpha(0.025).toString()
        };
      }
      return {
        ...errorVars,
        '--ci-color-border': getThemeColor(borderColor.value, theme.value),
        '--ci-color-border-focus': highlightColor,
        '--ci-color-bg-focus': `linear-gradient(${focusColor}, ${focusColor}), linear-gradient(#fff, #fff)`
      };
    });
    const inputAttrs = computed(() => {
      return {
        ...attrs,
        tabindex: 0,
        placeholder: placeholder.value,
        type: isTextarea.value ? null : type.value,
        maxlength: maxlength.value,
        disabled: disabled.value === true,
        readonly: readonly.value === true,
        autocomplete: autocomplete.value,
        pattern: pattern.value,
        ariaDisabled: ariaDisabled.value,
        ariaErrorMessage: ariaErrorMessage.value,
        ariaInvalid: ariaInvalid.value,
        ariaRequired: ariaRequired.value
      };
    });
    function handleFocus(e) {
      emit('focus', e);
      isFocused.value = true;
    }
    function handleBlur(e) {
      emit('blur', e);
      isFocused.value = false;
    }
    function focus() {
      input.value.focus();
    }
    function clear() {
      input.value.value = '';
    }
    function isValid() {
      return input.value.validity.valid;
    }
    return {
      localValue,
      input,
      inputClasses,
      styles,
      inputAttrs,
      handleFocus,
      handleBlur,
      focus,
      clear,
      inputId,
      isValid
    };
  }
};
export { InputProps, script as default };